import React, { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Cache equity data per country so the badge doesn't refetch needlessly.
const _eqCache = {};

export function FairPriceBadge({ compact = false, className = "", align = "end" }) {
  const { country, countryObj } = useApp();
  const key = country || "anon";
  const [eq, setEq] = useState(_eqCache[key] || null);
  useEffect(() => {
    if (_eqCache[key]) { setEq(_eqCache[key]); return; }
    let cancel = false;
    api.get("/equity/overview", { params: country ? { country } : {} })
      .then(({ data }) => { _eqCache[key] = data; if (!cancel) setEq(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [key, country]);
  if (!eq) return null;
  const region = countryObj?.name || (eq.country && eq.country !== "??" && eq.country.toLowerCase() !== "unknown" ? eq.country : "Global");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="fair-price-badge"
          className={`inline-flex items-center gap-1.5 rounded-full border border-rud px-2.5 py-1 text-[11px] text-sec hover:text-brand hover:border-brand/50 transition-colors ${className}`}
        >
          <Globe size={12} className="text-brand" />
          {compact ? `Fair price · ${region}` : `Fair price for ${region}`}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="glass border-rud text-[var(--rud-text)] w-72" data-testid="fair-price-popover">
        <div className="text-[10px] uppercase tracking-widest text-brand mb-1">Fair pricing for your region</div>
        <div className="font-serif text-lg leading-tight">
          {region}
          {eq.group_label && <span className="text-xs text-sec"> · {eq.group_label}</span>}
        </div>
        <p className="text-xs text-sec mt-2 leading-relaxed">{eq.message}</p>
        <div className="text-[11px] text-sec mt-2 pt-2 border-t border-rud leading-relaxed">
          Prices are automatically adjusted to your region's cost of living, so My Kind stays fair for everyone.
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FairPriceBadge;
